import { useEffect } from 'react';
import type { Virtualizer } from '@tanstack/react-virtual';
import { _isNil } from '@/littledash';
import { type DataTableCellSelectionChangeEvent, DataTableEvent, type DataTableService } from '../DataTable.model';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';

interface UseAutoScrollOptions {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  dataTableService: DataTableService;
  pinnedColumnCount: number;
}

const validateDimensions = (
  cellHeight: number,
  cellWidth: number,
  coordinate: { row: number; column: number }
): boolean => {
  if (cellHeight <= 0 || !Number.isFinite(cellHeight)) {
    ExceptionHandler.captureException(
      new InVivoError('Invalid cellHeight for auto-scroll calculation', {
        slug: 'datatable-auto-scroll-validation',
        level: 'warning',
        context: {
          cellHeight: String(cellHeight),
          cellWidth: String(cellWidth),
          row: String(coordinate.row),
          column: String(coordinate.column),
        },
      })
    );
    return false;
  }

  if (cellWidth <= 0 || !Number.isFinite(cellWidth)) {
    ExceptionHandler.captureException(
      new InVivoError('Invalid cellWidth for auto-scroll calculation', {
        slug: 'datatable-auto-scroll-validation',
        level: 'warning',
        context: {
          cellHeight: String(cellHeight),
          cellWidth: String(cellWidth),
          row: String(coordinate.row),
          column: String(coordinate.column),
        },
      })
    );
    return false;
  }

  return true;
};

export const useAutoScroll = ({
  rowVirtualizer,
  columnVirtualizer,
  dataTableService,
  pinnedColumnCount,
}: UseAutoScrollOptions): void => {
  useEffect(() => {
    const abortController = new AbortController();

    const handleSelectionChange = (event: DataTableCellSelectionChangeEvent): void => {
      try {
        const { scrollTo, activate, selection } = event.detail;

        // Only auto-scroll for keyboard navigation
        if (scrollTo || activate) {
          return;
        }

        const scrollElement = rowVirtualizer.scrollElement;
        if (_isNil(scrollElement)) {
          return;
        }

        const { from } = selection;
        const { cellHeight, cellWidth } = dataTableService.dimensions;

        // Validate dimensions before calculations
        if (!validateDimensions(cellHeight, cellWidth, from)) {
          return;
        }

        // Calculate visible viewport boundaries from scroll position
        const visibleRowStart = Math.floor(scrollElement.scrollTop / cellHeight);
        const visibleRowEnd = visibleRowStart + Math.ceil(scrollElement.clientHeight / cellHeight) - 1;

        const visibleColumnStart = Math.floor(scrollElement.scrollLeft / cellWidth);
        const visibleColumnEnd = visibleColumnStart + Math.ceil(scrollElement.clientWidth / cellWidth) - 1;

        // Adjust for pinned columns (selection uses absolute indices, virtualizer uses relative)
        const scrollableColumnIndex = from.column - pinnedColumnCount;

        // Check if selection is at viewport edges
        const needsVerticalScroll = from.row <= visibleRowStart || from.row >= visibleRowEnd;
        const needsHorizontalScroll =
          scrollableColumnIndex >= 0 &&
          (scrollableColumnIndex <= visibleColumnStart || scrollableColumnIndex >= visibleColumnEnd);

        if (needsVerticalScroll) {
          rowVirtualizer.scrollToIndex(from.row, { align: 'auto' });
        }

        if (needsHorizontalScroll) {
          columnVirtualizer.scrollToIndex(scrollableColumnIndex, { align: 'auto' });
        }
      } catch (error) {
        ExceptionHandler.captureException(
          new InVivoError('Auto-scroll handler failed', {
            cause: error,
            slug: 'datatable-auto-scroll-handler',
            level: 'warning',
            context: {
              row: `${event.detail.selection.from.row}`,
              column: `${event.detail.selection.from.column}`,
              scrollTo: `${event.detail.scrollTo}`,
              activate: `${event.detail.activate}`,
            },
          })
        );
      }
    };

    dataTableService.subscribe(DataTableEvent.CellSelectionChange, handleSelectionChange, abortController.signal);

    return () => {
      abortController.abort('teardown');
    };
  }, [rowVirtualizer, columnVirtualizer, dataTableService, pinnedColumnCount]);
};
